import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { pick } from "../i18n/translations";

const fmtPrice = (n) => (n == null ? "" : new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(Number(n)));

/**
 * Carrousel commercial horizontal — swipe mobile / flèches desktop.
 * Utilisé pour "Promotions du moment" et "Best-sellers".
 */
export default function ProductCarousel({ title, subtitle, items = [], emptyMessage, showPercent = false, badge = null, testId = "carousel" }) {
  const scrollerRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(true);

  const updateArrows = () => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 8);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
  };

  useEffect(() => {
    updateArrows();
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateArrows, { passive: true });
    return () => el.removeEventListener("scroll", updateArrows);
  }, [items]);

  const scroll = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.85, behavior: "smooth" });
  };

  if (!items || items.length === 0) {
    return emptyMessage ? (
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8 text-center text-sm text-ink-soft" data-testid={`${testId}-empty`}>
        <h2 className="font-display text-2xl md:text-3xl text-pedablue mb-2">{title}</h2>
        <p>{emptyMessage}</p>
      </section>
    ) : null;
  }

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid={testId}>
      <div className="flex items-end justify-between mb-6 gap-4">
        <div>
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl text-pedablue">{title}</h2>
          {subtitle && <p className="text-sm text-ink-soft mt-1">{subtitle}</p>}
        </div>
        <div className="hidden sm:flex gap-2">
          <button
            onClick={() => scroll(-1)}
            disabled={!canLeft}
            aria-label="Précédent"
            data-testid={`${testId}-prev`}
            className="w-10 h-10 rounded-full bg-white border border-ink/10 flex items-center justify-center hover:bg-ivory-100 disabled:opacity-30 disabled:cursor-not-allowed"
          ><ChevronLeft size={18} /></button>
          <button
            onClick={() => scroll(1)}
            disabled={!canRight}
            aria-label="Suivant"
            data-testid={`${testId}-next`}
            className="w-10 h-10 rounded-full bg-white border border-ink/10 flex items-center justify-center hover:bg-ivory-100 disabled:opacity-30 disabled:cursor-not-allowed"
          ><ChevronRight size={18} /></button>
        </div>
      </div>
      <div
        ref={scrollerRef}
        className="flex gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-4 -mx-4 px-4 sm:mx-0 sm:px-0"
        style={{ scrollbarWidth: "thin" }}
      >
        {items.map((p) => {
          const isResource = p.resource_kind === "resource" || p.resource_kind === "pack";
          const linkPath = isResource ? `/ressources-a-telecharger/${p.slug}` : `/produit/${p.slug}`;
          const price = p.sale_price || p.price || 0;
          const originalPrice = p.sale_price ? p.price : null;
          const pct = originalPrice ? Math.round((1 - price / originalPrice) * 100) : 0;
          return (
            <Link
              key={p.id}
              to={linkPath}
              className="group flex-shrink-0 w-[62%] sm:w-64 md:w-72 snap-start bg-white rounded-2xl border border-ink/10 overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all"
              data-testid={`${testId}-item-${p.slug}`}
            >
              <div className="aspect-[4/3] bg-ivory-100 relative overflow-hidden">
                {p.images?.[0] && (
                  <img src={p.images[0]} alt={pick(p.name)} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                )}
                {badge && (
                  <span className="absolute top-2 left-2 bg-pedablue text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full">
                    {badge}
                  </span>
                )}
                {showPercent && pct > 0 && (
                  <span className="absolute top-2 right-2 bg-rose-500 text-white text-xs font-black px-2 py-1 rounded-full">
                    -{pct}%
                  </span>
                )}
              </div>
              <div className="p-3">
                <p className="text-sm font-medium line-clamp-2 mb-2 leading-snug min-h-[2.5rem]">{pick(p.name)}</p>
                <div className="flex items-baseline gap-2">
                  <span className="font-display text-lg text-pedablue">{fmtPrice(price)}</span>
                  {originalPrice && <span className="text-xs line-through text-ink-soft">{fmtPrice(originalPrice)}</span>}
                  {p.is_digital && <span className="text-[9px] uppercase tracking-widest text-ink-soft ml-auto">PDF</span>}
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
